import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { PartnerGoal, PartnerGoalCreateInput } from "@colab/shared";

type CreateGoalResponse = { id: string; goal: PartnerGoal };

// POST /api/admin/goals
//
// Pessimistic — the new row has server-issued id + created_at; a synthetic
// optimistic row would diverge until refetch. Form-shaped save, no
// latency-visible toggle UI to optimise for.
export function useCreateGoal() {
  const queryClient = useQueryClient();
  return useMutation<CreateGoalResponse, ApiClientError, PartnerGoalCreateInput>({
    mutationFn: (input) =>
      apiFetch<CreateGoalResponse>("/admin/goals", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.goals.list() });
      if (data.goal.partner_id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.goals.forPartner(data.goal.partner_id),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.partners.detail(data.goal.partner_id),
        });
      }
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to create goal");
    },
  });
}
